import { useApi } from '../lib/hooks'
import { money, num, titleCase } from '../lib/format'
import { Banner, Delta, Empty, ErrorNote, Loading, Panel, Pill, StatMini } from '../ui/kit'

interface ShrinkCause {
  reasonCode: string; reasonLabel: string | null; bucket: string
  isFraudSignal: boolean; lots: number; kg: number; valueKes: number; sharePct: number | null
}

interface ShrinkReport {
  causes: ShrinkCause[]
  /** How many lots actually carry a variance in this window. */
  lotsWithVariance: number
  /** How many lots were weighed all the way through, so could have carried one. */
  lotsWeighedThrough: number
  /** Below this the shape of the losses is not yet worth drawing conclusions from. */
  minimumSampleForInference: number
}

interface AgentRow {
  userId: string; agentName: string; lots: number
  purchasedKg: number; purchasedValue: number
  avgVariancePct: number | null; variabilityPct: number | null
  unexplainedCount: number; avgPricePaid: number | null; priceOverrides: number
}

export function Reports() {
  const shrink = useApi<ShrinkReport>('/ops/shrink')
  const agents = useApi<AgentRow[]>('/ops/agent-variance?days=30')

  const rows = shrink.data?.causes ?? []
  const totalKg = rows.reduce((s, r) => s + Number(r.kg ?? 0), 0)
  const totalValue = rows.reduce((s, r) => s + Number(r.valueKes ?? 0), 0)
  const unexplained = rows.find((r) => r.reasonCode === 'UNEXPLAINED')
  const unexplainedShare = Number(unexplained?.sharePct ?? 0)

  // A claim about the shape of the losses needs enough lots behind it. On a handful,
  // "unexplained is zero" says nothing except that few lots have been reconciled yet.
  const sample = shrink.data?.lotsWithVariance ?? 0
  const minimumSample = shrink.data?.minimumSampleForInference ?? 25
  const enoughToJudge = sample >= minimumSample

  return (
    <main className="page">
      <div className="page-head">
        <h1>Weight lost</h1>
        <p>
          Produce loses weight on the way — it dries out, it gets damaged, some is thrown away. Each
          reason is recorded separately so theft can never be quietly filed as spoilage. Watch the
          unexplained figure: a little is normal, and exactly zero usually means somebody is putting
          a reason on everything.
        </p>
      </div>

      <div className="stack">
        <Panel
          title="Why weight was lost"
          meta="last 30 days"
          footer={
            <>
              <StatMini label="Total weight lost" value={`${num(totalKg, 1)} kg`} />
              <StatMini label="Value" value={money(totalValue, { compact: true })} />
              <StatMini
                label="Unexplained share"
                value={`${unexplainedShare.toFixed(1)}%`}
                note={
                  !enoughToJudge
                    ? 'too few lots to read'
                    : unexplainedShare === 0
                      ? 'suspiciously clean'
                      : unexplainedShare > 25
                        ? 'investigate'
                        : 'within reason'
                }
              />
              <StatMini
                label="Collections with a difference"
                value={sample}
                note={enoughToJudge ? 'enough to go on' : `need ${minimumSample} before judging`}
              />
            </>
          }
        >
          {shrink.error && <ErrorNote error={shrink.error} onRetry={shrink.reload} />}
          {shrink.loading && <Loading rows={4} />}
          {shrink.data && rows.length === 0 && (
            <Empty title="No losses recorded yet">
              Shrink appears once lots have been weighed at both the farmgate and the depot.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="panel-body col" style={{ gap: 10 }}>
              {rows.map((r) => (
                <div key={r.reasonCode}>
                  <div className="spread" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                      {r.reasonLabel ?? titleCase(r.reasonCode)}
                      {r.isFraudSignal && <> <Pill tone="crit" dot={false}>fraud signal</Pill></>}
                    </span>
                    <span className="num" style={{ fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                      {num(r.kg, 1)} kg · {money(r.valueKes, { currency: false })}
                    </span>
                  </div>
                  <div className={`meter ${r.isFraudSignal ? 'is-crit' : ''}`} style={{ marginTop: 0 }}>
                    <i style={{ width: `${Math.min(100, Number(r.sharePct ?? 0))}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {totalKg > 0 && !enoughToJudge && (
          <Banner tone="info" title={`Only ${sample} lot${sample === 1 ? '' : 's'} carry a variance so far`}>
            The breakdown above is real, but it is too small a sample to read anything into the shape
            of it. At {minimumSample} lots this page starts flagging patterns — an unexplained share
            that is suspiciously clean, or one cause that has quietly taken over.
          </Banner>
        )}

        {totalKg > 0 && enoughToJudge && unexplainedShare === 0 && (
          <Banner tone="warn" title="Nothing at all is unexplained">
            Across {sample} lots that is rarely true in a real operation. Either the tolerance is set
            too wide, or reasons are being assigned to make the number go away. Spot-check a few lots
            against their photos.
          </Banner>
        )}

        <Panel
          title="Missing weight, by agent"
          meta="last 30 days — one agent always losing more than the rest is worth a conversation, not an accusation"
        >
          {agents.loading && <Loading rows={4} />}
          {agents.data?.length === 0 && (
            <Empty title="No agent activity in this window" />
          )}
          {agents.data && agents.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Agent</th><th className="n">Collections</th><th className="n">Bought</th>
                    <th className="n">Value</th><th className="n">Avg difference</th>
                    <th className="n">How steady</th><th className="n">Unexplained</th>
                    <th className="n">Avg price</th><th className="n">Price changed</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.data.map((a) => (
                    <tr key={a.userId} className={a.unexplainedCount > 0 || a.priceOverrides > 2 ? 'attention' : ''}>
                      <td className="strong truncate">{a.agentName}</td>
                      <td className="n">{a.lots}</td>
                      <td className="n">{num(a.purchasedKg)} kg</td>
                      <td className="n">{money(a.purchasedValue, { currency: false })}</td>
                      <td className="n"><Delta value={a.avgVariancePct} goodDirection="zero" decimals={2} /></td>
                      <td className="n faint">{a.variabilityPct === null ? '—' : `±${Number(a.variabilityPct).toFixed(2)}%`}</td>
                      <td className="n">
                        {a.unexplainedCount > 0
                          ? <Pill tone="crit" dot={false}>{a.unexplainedCount}</Pill>
                          : <span className="faint">0</span>}
                      </td>
                      <td className="n">{money(a.avgPricePaid, { currency: false })}</td>
                      <td className="n">
                        {a.priceOverrides > 0
                          ? <Pill tone="warn" dot={false}>{a.priceOverrides}</Pill>
                          : <span className="faint">0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Banner tone="info" title="How to use this">
          Swap agents between areas and see whether the losses follow the person or stay with the
          route. If they follow the person, the road is not the problem.
        </Banner>
      </div>
    </main>
  )
}
